// Shared redirect-authorize logic for Discord SSO, used by the login button
// (src/components/modals/sso/discord-sso-button.tsx — also used in desktop mode from
// src/pages/auth/desktop.tsx), and the "Connect" action on the connections page
// (src/pages/dashboard/connections.tsx). The main difference between callers is the `mode`
// ('login' | 'link' | 'desktop') and `returnTo` carried in the OAuth `state` param —
// src/pages/auth/discord/callback.tsx branches on `mode` once Discord redirects back.

import { encodeOAuthState, generateCsrfToken } from '@/utils/oauth-state';

export const DISCORD_CSRF_STORAGE_KEY = 'sso_discord_csrf';
export const DISCORD_AUTHORIZE_URL = 'https://discord.com/api/oauth2/authorize';
export const DISCORD_CALLBACK_PATH = '/auth/discord/callback';
export const DISCORD_SCOPES = 'identify email';

interface BeginDiscordAuthorizeParams {
  mode: 'login' | 'link' | 'desktop' | 'mobile';
  returnTo: string;
  locale?: string;
  // Required (and must already be validated by the caller with isSafeDesktopRedirect) when
  // mode === 'desktop' — carried in the OAuth `state` so the callback knows where to hand off
  // the login-token. Ignored for 'login'/'link'.
  desktopRedirectUri?: string;
  // Required (and must already be validated by the caller with parseCbHttpPort) when
  // mode === 'mobile' — the loopback port carried in the OAuth `state` so the callback can hand
  // the mobile-login shape to the app's local listener. Ignored for other modes.
  cbHttpPort?: number;
  // Invoked right before the browser navigates away (e.g. to show a loading overlay).
  onBeforeRedirect?: () => void;
}

// Builds the Discord authorize URL, stores the CSRF token, and navigates the browser there.
// Returns false without doing anything when the client ID isn't configured.
export function beginDiscordAuthorize({ mode, returnTo, locale, desktopRedirectUri, cbHttpPort, onBeforeRedirect }: BeginDiscordAuthorizeParams): boolean {
  const clientId = process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID ?? '';
  if (!clientId) return false;

  const csrf = generateCsrfToken();
  const state = encodeOAuthState({ csrf, returnTo, locale, mode, redirectUri: mode === 'desktop' ? desktopRedirectUri : undefined, cbHttpPort: mode === 'mobile' ? cbHttpPort : undefined });

  try {
    sessionStorage.setItem(DISCORD_CSRF_STORAGE_KEY, csrf);
  } catch {
    // sessionStorage pode estar indisponível (modo privado, quota). Sem ele, o callback
    // vai considerar o state inválido — o pior caso é o usuário ter que tentar novamente.
  }

  if (onBeforeRedirect) onBeforeRedirect();

  const redirectUri = `${window.location.origin}${DISCORD_CALLBACK_PATH}`;
  const authorizeUrl = new URL(DISCORD_AUTHORIZE_URL);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('client_id', clientId);
  authorizeUrl.searchParams.set('scope', DISCORD_SCOPES);
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('state', state);
  authorizeUrl.searchParams.set('prompt', 'consent');

  window.location.href = authorizeUrl.toString();
  return true;
}
